---
title: Getting started
description: Make your first Storefront API request with your store's publishable key.
order: 1
---

The Storefront API is what a headless storefront calls to show the catalog, build a cart, check
out and manage a shopper's account. It serves one store at a time: the API key you send decides
which store answers, so there is no store id in any URL.

## Base URL

Every path in these docs starts with `/storefront/v1` on the same API host your admin talks to.
The examples use `https://api.example.com`; replace it with your API's address.

```bash title="Shell"
export NUCLEUS_API_URL=https://api.example.com
export NUCLEUS_KEY=YOUR_PUBLISHABLE_KEY
```

The curl examples read these two variables. The fetch and SDK examples use constants with the same
names.

## Get a key

<Steps>

### Create a publishable key

In the admin, open **Developers → API keys** and create a **publishable** key. Add every origin
your storefront runs on under **Allowed origins** (for example `http://localhost:3001` and
`https://shop.example.com`). The key is shown once; copy it then.

### Or use a playground key here

Open any endpoint page in the [API reference](/docs/api). Its **Try it** panel creates a playground
key for your store that works from this page, and every example on the endpoint pages uses it.
Your selected key is <ApiKeyInline />.

</Steps>

<Callout type="warning" title="Secret keys stay on a server">
  A **secret** key (`sk_…`) is for server code only; the API refuses it from a browser. See
  [Authentication](/docs/guides/authentication) for how the two kinds differ.
</Callout>

## Make your first request

<EndpointLink id="getStore" /> returns the store's profile: name, currencies, locales, checkout
settings and which sign-in methods are switched on. It is a good first call because it needs nothing
but the key.

<CodeTabs>

```bash title="curl"
curl "$NUCLEUS_API_URL/storefront/v1/store" \
  -H "Authorization: Bearer $NUCLEUS_KEY"
```

```ts title="fetch"
const NUCLEUS_API_URL = 'https://api.example.com';
const NUCLEUS_KEY = 'YOUR_PUBLISHABLE_KEY';

const store = await fetch(`${NUCLEUS_API_URL}/storefront/v1/store`, {
  headers: { Authorization: `Bearer ${NUCLEUS_KEY}` },
}).then((response) => response.json());
```

```ts title="SDK"
import { createStorefrontClient } from '@nucleus-commerce/storefront-sdk';

const NUCLEUS_API_URL = 'https://api.example.com';
const NUCLEUS_KEY = 'YOUR_PUBLISHABLE_KEY';

const storefront = createStorefrontClient({
  baseUrl: NUCLEUS_API_URL,
  publishableKey: NUCLEUS_KEY,
});

const store = await storefront.getStore();
```

</CodeTabs>

The response is the store as JSON (shortened here):

```json title="Response"
{
  "id": "01a0f3a2-2cba-7058-ba62-4556c8e9d495",
  "name": "Aurora Jewels",
  "defaultCurrency": "USD",
  "supportedCurrencies": ["USD"],
  "defaultLocale": "en-US",
  "checkout": { "guestCheckout": true, "requirePhone": false },
  "signInProviders": { "password": true, "magicLink": true, "google": true }
}
```

A `401` means the key is missing or wrong; a `403` with `ORIGIN_NOT_ALLOWED` means a browser sent
the request from an origin the key does not list. Both are covered in
[Authentication](/docs/guides/authentication).

## Install the SDK

The TypeScript SDK wraps every endpoint with typed requests and responses. It runs in the browser
with a publishable key and on a server with a secret key.

```bash title="Shell"
pnpm add @nucleus-commerce/storefront-sdk
```

Each SDK function is named after the endpoint's operation id, the same name that ends every
endpoint page's URL: <EndpointLink id="addCartLine" /> is `storefront.addCartLine(...)`. Path
parameters come first as one object, then the body, then query parameters. Every endpoint page
shows the call in its **SDK** tab.

## Where to go next

| Page                                                | What it covers                                                    |
| --------------------------------------------------- | ----------------------------------------------------------------- |
| [Authentication](/docs/guides/authentication)       | Key kinds, allowed origins, scopes, shopper sessions, rate limits |
| [Conventions](/docs/guides/conventions)             | Money, pagination, errors, markets, caching                       |
| [Build a product page](/docs/guides/product-page)   | Store, product, stock, rails and SEO for one page                 |
| [Cart to checkout](/docs/guides/cart-to-checkout)   | Cart, discounts, shipping, payment and the order status page      |
| [Customer accounts](/docs/guides/customer-accounts) | Sign-in methods, profile, addresses, orders, wishlist, privacy    |
| [API reference](/docs/api)                          | Every endpoint with parameters, schemas, examples and try-it      |
| [Postman](/docs/postman)                            | A collection and environment to call the API from Postman         |
| [Build with AI](/docs/build)                        | Prompts that have an AI coding agent build the storefront for you |
